import {
  type KVCameraPoseCalibration,
  type KVCameraPosesByFilename,
  PhotoResponse,
  WorldPosition,
} from 'types'

const distortBrownConrady = (
  x: number,
  y: number,
  k1: number,
  k2: number,
  k3: number,
  k4: number,
  t1: number,
  t2: number
): [number, number] => {
  const r2 = x * x + y * y
  const r4 = r2 * r2
  const r6 = r4 * r2
  const r8 = r4 * r4
  const radial = 1.0 + k1 * r2 + k2 * r4 + k3 * r6 + k4 * r8
  const x_tan = 2.0 * t1 * x * y + t2 * (r2 + 2.0 * x * x)
  const y_tan = t1 * (r2 + 2.0 * y * y) + 2.0 * t2 * x * y
  const xd = x * radial + x_tan
  const yd = y * radial + y_tan
  return [xd, yd]
}

const isPointInImage = (
  u: number,
  v: number,
  imgWidth: number,
  imgHeight: number
): boolean => {
  return u >= 0 && u < imgWidth && v >= 0 && v < imgHeight
}

const matrixVectorMultiply = (
  matrix: [
    [number, number, number],
    [number, number, number],
    [number, number, number],
  ],
  vector: [number, number, number]
): [number, number, number] => {
  return [
    matrix[0][0] * vector[0] +
      matrix[0][1] * vector[1] +
      matrix[0][2] * vector[2],
    matrix[1][0] * vector[0] +
      matrix[1][1] * vector[1] +
      matrix[1][2] * vector[2],
    matrix[2][0] * vector[0] +
      matrix[2][1] * vector[1] +
      matrix[2][2] * vector[2],
  ]
}

const vectorSubtract = (
  a: [number, number, number],
  b: [number, number, number]
): [number, number, number] => {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]]
}

const vectorNorm = (v: [number, number, number]): number => {
  return Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2])
}

const calculateContributionScore = (
  distanceToCamera: number,
  angleToCamera: number
): number => {
  const DISTANCE_WEIGHT = 1.0
  const ANGLE_WEIGHT = 0.75
  return DISTANCE_WEIGHT * distanceToCamera + ANGLE_WEIGHT * angleToCamera
}

const projectPointIntoCamera = (
  worldPoint: WorldPosition,
  cam: KVCameraPoseCalibration
): PhotoResponse | null => {
  const worldPointArray: [number, number, number] = [
    worldPoint.x,
    -1 * worldPoint.y, // convert to reality capture units
    worldPoint.z,
  ]
  const distanceToCamera = vectorSubtract(worldPointArray, cam.cam_location)
  const cameraPt = matrixVectorMultiply(cam.rotation_matrix, distanceToCamera)
  const cameraPtZ = cameraPt[2]

  if (cameraPtZ <= 0) {
    return null
  }

  const xUndistorted = cameraPt[0] / cameraPtZ
  const yUndistorted = cameraPt[1] / cameraPtZ

  const [xDistorted, yDistorted] = distortBrownConrady(
    xUndistorted,
    yUndistorted,
    cam.k1,
    cam.k2,
    cam.k3,
    cam.k4,
    cam.t1,
    cam.t2
  )

  const u = cam.focal_length_x * xDistorted + cam.center_x
  const v = cam.focal_length_y * yDistorted + cam.center_y

  if (!isPointInImage(u, v, cam.width, cam.height)) {
    return null
  }

  const camAngleToPt =
    Math.atan(
      Math.sqrt(xUndistorted * xUndistorted + yUndistorted * yUndistorted)
    ) *
    (180 / Math.PI)
  const camDistToPt = vectorNorm(
    vectorSubtract(worldPointArray, cam.cam_location)
  )

  const contributionScore = calculateContributionScore(
    camDistToPt,
    camAngleToPt
  )

  return {
    image_name: cam.name,
    width: cam.width,
    height: cam.height,
    contribution_score: contributionScore,
    projection: {
      x: u,
      y: v,
    },
  }
}

export const fetchNearbyPhotos = async (
  position: WorldPosition,
  cameraPosesByFilename: KVCameraPosesByFilename
): Promise<PhotoResponse[]> => {
  const photoResponses: PhotoResponse[] = []

  for (const [name, cam] of Object.entries(cameraPosesByFilename)) {
    const photoResponse = projectPointIntoCamera(position, {
      ...cam,
      name: name,
    })
    if (photoResponse) {
      photoResponses.push(photoResponse)
    }
  }

  photoResponses.sort((a, b) => a.contribution_score - b.contribution_score)

  return photoResponses
}
